import {
  Component,
  EventEmitter,
  Input,
  Output,
  inject,
  signal,
  ChangeDetectionStrategy
} from '@angular/core';

import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { IconComponent } from '../../../../../shared/components/icon/icon.component';
import { IconType } from '../../../../../shared/services/icon.service';

/**
 * Login-email change for password-based accounts only. Mirrors the structural
 * pattern of `ChangePasswordComponent`: collapsible card, current-password
 * gate, single-shot emit to the parent. OAuth users don't see this section
 * (the parent gates with `@if (!isOAuthUser())`) since the email lives on the
 * OAuth provider — the backend would reject with `PROFILE_OAUTH_EMAIL_FORBIDDEN`
 * anyway.
 */
@Component({
  selector: 'app-change-email',
  standalone: true,
  imports: [ReactiveFormsModule, IconComponent],
  templateUrl: './change-email.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './change-email.component.css',
})
export class ChangeEmailComponent {
  /** Currently displayed in the read-only "current email" hint at the top. */
  @Input() currentEmail: string | null | undefined;

  @Output() changeEmail = new EventEmitter<{
    currentPassword: string;
    email: string;
  }>();

  private fb = inject(FormBuilder);

  readonly IconType = IconType;
  isExpanded = signal(false);
  isSaving = signal(false);
  showCurrentPassword = signal(false);

  emailForm: FormGroup = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    currentPassword: ['', [Validators.required]],
  });

  toggleExpanded(): void {
    this.isExpanded.update((v) => !v);
    if (!this.isExpanded()) {
      this.emailForm.reset();
    }
  }

  togglePasswordVisibility(): void {
    this.showCurrentPassword.update((v) => !v);
  }

  onSubmit(): void {
    if (!this.emailForm.valid || this.isSaving()) return;
    const { email, currentPassword } = this.emailForm.value;
    if (email === this.currentEmail) {
      // Don't bother the server with a no-op; just collapse.
      this.isExpanded.set(false);
      return;
    }
    this.isSaving.set(true);
    this.changeEmail.emit({ email, currentPassword });
  }

  onSaveComplete(success: boolean): void {
    this.isSaving.set(false);
    if (success) {
      this.emailForm.reset();
      this.isExpanded.set(false);
    }
  }

  hasError(field: string, error: string): boolean {
    const control = this.emailForm.get(field);
    return !!(control?.touched && control?.hasError(error));
  }
}
